import { useMemo } from 'react';
import type { ComponentPropsWithoutRef, FC } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { Skeleton } from '../skeleton';

import type { DisplayNameProps } from './index';
import { DisplayNameWithoutDomain } from './no-domain';

const messages = defineMessages({
  invalidHandle: {
    id: 'account.hame.invalid_handle',
    defaultMessage: 'Handle unavailable',
  },
});

export function useAccountHandle(
  account: DisplayNameProps['account'],
  localDomain?: DisplayNameProps['localDomain'],
) {
  const intl = useIntl();

  return useMemo(() => {
    if (!account) {
      return null;
    }

    if (account.invalid_handle)
      return intl.formatMessage(messages.invalidHandle);

    let acct = account.acct;

    if (!acct.includes('@') && localDomain) {
      acct = `${acct}@${localDomain}`;
    }

    return `@${acct}`;
  }, [account, localDomain, intl]);
}

export const DisplayNameDefault: FC<
  Omit<DisplayNameProps, 'variant'> & ComponentPropsWithoutRef<'span'>
> = ({ account, displayPronouns, localDomain, className, ...props }) => {
  const username = useAccountHandle(account, localDomain);

  const pronounsClickHandler = event => {
    for (let max = 5, el = event.target; --max && el; el = el.parentElement) {
      if (el.classList?.contains('display-name__pronouns')) { break; }
      if (el?.tagName === "A") { event.stopPropagation(); break; }
    }
  };

  return (
    <DisplayNameWithoutDomain
      account={account}
      className={className}
      {...props}
    >
      {' '}
      <span className='display-name__account'>
        {username ?? <Skeleton width='7ch' />}
        {displayPronouns && account.pronouns &&
          <> · <span class='display-name__pronouns' onClick={pronounsClickHandler} dangerouslySetInnerHTML={{ __html: account.pronouns }} /> </>
        }
      </span>
    </DisplayNameWithoutDomain>
  );
};
